/* eslint-disable no-restricted-syntax */
import { trigger, style, animate, transition } from '@angular/animations';
import {
  Component,
  Input,
  ViewEncapsulation,
  ViewChild,
  HostListener,
  ContentChild,
  TemplateRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { LineSeriesComponent, ColorHelper, BarVerticalStackedComponent } from '@swimlane/ngx-charts';
import { scaleLinear, scalePoint } from 'd3-scale';
import { curveLinear } from 'd3-shape';

@Component({
  // tslint:disable-next-line: component-selector
  selector: 'combo-chart-component',
  templateUrl: './combo-chart.component.html',
  styleUrls: ['./combo-chart.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('animationState', [
      transition(':leave', [
        style({
          opacity: 1,
          transform: '*',
        }),
        animate(500, style({ opacity: 0, transform: 'scale(0)' })),
      ]),
    ]),
  ],
})
export class ComboChartComponent extends BarVerticalStackedComponent {
  @Input() curve: any = curveLinear;
  @Input() yAxisLabelRight;
  @Input() lineChart: any;
  @Input() lineScheme: any;

  @ContentChild('seriesTooltipTemplate') seriesTooltipTemplate: TemplateRef<any>;

  @ViewChild(LineSeriesComponent) lineSeriesComponent: LineSeriesComponent;

  colorsLine: ColorHelper;
  scaleType = 'linear';
  xScaleLine;
  xDomainLine;
  yDomainLine;
  scaledAxis;
  combinedSeries;
  xSet;
  filteredDomain;
  hoveredVertical;
  yOrientLeft = 'left';
  yOrientRight = 'right';

  update(): void {
    this.yDomainLine = this.getYDomainLine();
    super.update();

    // line chart
    this.xDomainLine = this.getXDomainLine();
    if (this.filteredDomain) {
      this.xDomainLine = this.filteredDomain;
    }

    this.xScaleLine = this.getXScaleLine();

    this.setColors();
    this.legendOptions = this.getLegendOptions();

    this.transform = `translate(${this.dims.xOffset} , ${this.margin[0]})`;
  }

  deactivateAll() {
    this.activeEntries = [...this.activeEntries];
    for (const entry of this.activeEntries) {
      this.deactivate.emit({ value: entry, entries: [] });
    }
    this.activeEntries = [];
  }

  @HostListener('mouseleave')
  hideCircles(): void {
    this.hoveredVertical = null;
    this.deactivateAll();
  }

  updateHoveredVertical(item): void {
    this.hoveredVertical = item.value;
    this.deactivateAll();
  }

  isDate(value): boolean {
    if (value instanceof Date) {
      return true;
    }

    return false;
  }

  getScaleType(values): string {
    let date = true;
    let num = true;

    for (const value of values) {
      if (!this.isDate(value)) {
        date = false;
      }

      if (typeof value !== 'number') {
        num = false;
      }
    }

    if (date) {
      return 'time';
    }
    if (num) {
      return 'linear';
    }
    return 'ordinal';
  }

  getXDomainLine(): any[] {
    let values = [];

    for (const results of this.lineChart) {
      for (const d of results.series) {
        if (!values.includes(d.name)) {
          values.push(d.name);
        }
      }
    }

    this.scaleType = this.getScaleType(values);
    let domain = [];

    if (this.scaleType === 'time') {
      const min = Math.min(...values);
      const max = Math.max(...values);
      domain = [min, max];
    } else if (this.scaleType === 'linear') {
      values = values.map((v) => Number(v));
      const min = Math.min(...values);
      const max = Math.max(...values);
      domain = [min, max];
    } else {
      domain = values;
    }

    this.xSet = values;
    return domain;
  }

  getYDomainLine(): any[] {
    const domain = [];

    for (const results of this.lineChart) {
      for (const d of results.series) {
        if (domain.indexOf(d.value) < 0) {
          domain.push(d.value);
        }
        if (d.min !== undefined) {
          if (domain.indexOf(d.min) < 0) {
            domain.push(d.min);
          }
        }
        if (d.max !== undefined) {
          if (domain.indexOf(d.max) < 0) {
            domain.push(d.max);
          }
        }
      }
    }

    let min = Math.min(...domain);
    const max = Math.max(...domain);
    min = Math.min(0, min);
    return [min, max];
  }

  getXScaleLine(): any {
    const barWidth = this.xScale.bandwidth() || 1;
    const offset = Math.max(
      (this.dims.width + this.barPadding - (barWidth + this.barPadding) * this.groupDomain.length) / 2,
      0,
    );

    return scalePoint()
      .range([offset + barWidth / 2, this.dims.width - offset - barWidth / 2])
      .domain(this.groupDomain);
  }

  getYScale(): any {
    const scale = scaleLinear().range([this.dims.height, 0]).domain(this.yDomainLine);
    return this.roundDomains ? scale.nice() : scale;
  }

  setColors(): void {
    super.setColors();
    this.colorsLine = new ColorHelper(
      (this.lineScheme as any) || this.scheme,
      this.schemeType,
      this.innerDomain,
      this.customColors,
    );
  }
}
